/**
 * arisCommands — Motor de comandos client-side para Aris (sin IA).
 *
 * Reglas: cada intent es una regex española tolerante a acentos. El parser
 * devuelve `{intent, payload}` o `null` si no reconoce.
 *
 * El componente Aris usa `runCommand(text, ctx)` que:
 *  1. parsea la intención,
 *  2. resuelve entidades contra el cache (pacientes),
 *  3. devuelve una `action` que Aris ejecuta (navegar, mostrar chips, etc).
 */

const norm = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

/**
 * Parsea una especificación de medicamento estilo español:
 * "ibuprofeno 400mg cada 8h por 5 días"
 * "amoxicilina 500 mg cada 8 horas por 7 dias"
 * "paracetamol 1g 3 veces al dia por 3 dias"
 * Devuelve {medication, dosage, frequency, duration} — campos vacíos si no
 * fue posible detectar.
 */
export function parseRxSpec(text) {
  const t = (text || "").trim().replace(/\s+/g, " ");
  if (!t) return null;

  const norml = norm(t);

  // duration: "por N dias|semanas|meses" o "N dias|semanas"
  let duration = "";
  const durRx =
    /(?:por\s+)?(\d+)\s*(dias?|semanas?|meses?|d|sem|m)\b/;
  const dm = norml.match(durRx);
  if (dm) {
    const n = dm[1];
    const unit = dm[2].startsWith("sem")
      ? "semanas"
      : dm[2].startsWith("m") && dm[2] !== "m"
        ? "meses"
        : dm[2] === "m"
          ? "meses"
          : "días";
    duration = `${n} ${unit}`;
  }

  // frequency: "cada N h|horas" | "N veces al dia" | "una vez al dia"
  let frequency = "";
  let fm = norml.match(/cada\s+(\d+)\s*(h|hs|hrs|horas?)\b/);
  if (fm) frequency = `cada ${fm[1]} horas`;
  if (!frequency) {
    fm = norml.match(/(\d+)\s*veces?\s+al\s+dia\b/);
    if (fm) frequency = `${fm[1]} veces al día`;
  }
  if (!frequency && /\buna?\s+vez\s+al\s+dia\b/.test(norml)) {
    frequency = "una vez al día";
  }

  // dosage: primer patrón "N mg|g|ml|mcg|ui|gotas" (con o sin espacio)
  let dosage = "";
  const doseRx = /(\d+(?:[.,]\d+)?)\s*(mg|g|ml|mcg|ui|gotas|gts|tabl?e?t?a?s?)\b/;
  const dosem = norml.match(doseRx);
  if (dosem) {
    dosage = `${dosem[1].replace(",", ".")} ${dosem[2]}`;
  }

  // medication: todo lo que aparece ANTES del dosage (o antes de "cada"/"por"/número).
  let medication = "";
  if (dosem) {
    medication = t.slice(0, dosem.index).trim();
    if (!medication) {
      // dosage al inicio — poco común, fallback: primera palabra tras "receta"
      medication = t.split(/\s+/)[0] || "";
    }
  } else {
    // sin dosage: heredar del token antes de "cada"/"por"/número
    const cut = norml.search(/\s(cada|por|\d)/);
    medication = cut > 0 ? t.slice(0, cut).trim() : t;
  }
  // Limpiar palabras conectoras al final ("con", "de")
  medication = medication.replace(/\s+(con|de)$/i, "").trim();

  return {
    medication,
    dosage,
    frequency,
    duration,
  };
}

// ---------- INTENT PATTERNS ----------
// Cada patrón captura opcionalmente un "target" (nombre del paciente).
const PATTERNS = [
  {
    intent: "show_patient",
    // "muéstrame el paciente Juan" / "muestrame a juan" / "abre el paciente juan"
    // "ficha de juan" / "abre a juan" / "ver a juan"
    rx: /^(?:muestra(?:me)?|muestra|abre|abrir|ver|dame|busca(?:me)?)\s+(?:el paciente|la paciente|al paciente|a la paciente|el expediente(?: de)?|la ficha(?: de)?|ficha(?: de)?|paciente|a)\s+(.+?)$/,
    action: "navigate_patient",
  },
  {
    intent: "show_patient_short",
    // "juan pérez" solo — si el input es 2-4 palabras y matchea un paciente
    rx: /^(?:ficha(?: de)?|expediente(?: de)?)\s+(.+?)$/,
    action: "navigate_patient",
  },
  {
    intent: "create_patient",
    rx: /^(?:crea(?:r)?|nuevo|nueva|agrega(?:r)?|registra(?:r)?)\s+(?:un\s+|una\s+)?paciente(?:\s+(.+))?$/,
    action: "navigate_new_patient",
  },
  {
    intent: "create_appointment",
    rx: /^(?:crea(?:r)?|agenda(?:r)?|nueva|nuevo|programa(?:r)?)\s+(?:una\s+|un\s+)?cita(?:\s+(?:para|con|de)\s+(.+))?$/,
    action: "navigate_new_appointment",
  },
  {
    intent: "add_rx_item",
    // "agrega receta ibuprofeno 400mg cada 8h por 5 dias"
    // "nueva receta amoxicilina ..."
    // "receta paracetamol ..."
    // DEBE ir antes de add_note para no ser interceptado por "agrega ...".
    rx: /^(?:(?:agrega(?:r)?|anade|añade|nueva|nuevo)\s+(?:una\s+)?)?receta[:,]?\s+(.+)$/,
    action: "add_rx_item",
  },
  {
    intent: "add_note",
    // "agrega esta nota <texto>" / "agrega nota <texto>" / "anota <texto>"
    rx: /^(?:agrega(?:r)?|anade|añade|añadir|anadir|apunta|anota|escribe)\s+(?:esta\s+)?(?:nota|apunte|comentario)?[:,]?\s*(.+)$/,
    action: "append_note",
  },
  {
    intent: "go_agenda",
    rx: /^(?:abrir?|muestra(?:me)?|ir a|ver)\s+(?:la\s+)?agenda$/,
    action: "navigate_agenda",
  },
  {
    intent: "go_patients",
    rx: /^(?:abrir?|muestra(?:me)?|ver|ir a)\s+(?:la\s+lista(?: de)?\s+)?pacientes$/,
    action: "navigate_patients",
  },
  {
    intent: "go_prescriptions",
    rx: /^(?:abrir?|muestra(?:me)?|ver|ir a)\s+(?:las\s+|mis\s+)?recetas$/,
    action: "navigate_prescriptions",
  },
  {
    intent: "start_consultation",
    rx: /^(?:inicia(?:r)?|empie(?:za|zar)|comien(?:za|zar)|nueva|abrir)\s+(?:la\s+|una\s+)?consulta(?:\s+(?:para|con|de)\s+(.+))?$/,
    action: "start_consultation",
  },
  {
    intent: "save_consultation",
    rx: /^(?:finaliza(?:r)?|guarda(?:r)?|termina(?:r)?|cierra(?:r)?|concluye(?:r)?)\s+(?:la\s+)?consulta$/,
    action: "save_consultation",
  },
  {
    intent: "cancel_consultation",
    rx: /^(?:cancela(?:r)?|descarta(?:r)?|abandona(?:r)?)\s+(?:la\s+)?consulta$/,
    action: "cancel_consultation",
  },
  {
    intent: "set_diagnosis",
    rx: /^(?:diagnostico|diagnosis|dx)[:,]?\s+(.+)$/,
    action: "set_diagnosis",
  },
  {
    intent: "set_symptoms",
    rx: /^(?:sintomas|symptoms|sx)[:,]?\s+(.+)$/,
    action: "set_symptoms",
  },
  {
    intent: "set_treatment",
    rx: /^(?:tratamiento|treatment|rx|indicaciones)[:,]?\s+(.+)$/,
    action: "set_treatment",
  },
  {
    intent: "help",
    rx: /^(?:ayuda|help|que puedes hacer|comandos)$/,
    action: "show_help",
  },
];

/**
 * Parsea una frase y devuelve `{intent, target, raw}` o `null`.
 */
export function parseCommand(text) {
  const clean = norm(text);
  if (!clean) return null;
  for (const p of PATTERNS) {
    const m = clean.match(p.rx);
    if (m) {
      return {
        intent: p.intent,
        action: p.action,
        target: (m[1] || "").trim(),
        raw: text.trim(),
      };
    }
  }
  return null;
}

/**
 * Ejecuta un comando ya parseado. Devuelve una acción para que Aris.jsx la
 * aplique: navegación, respuesta textual, chips de desambiguación, etc.
 *
 * ctx = { search: (q) => Patient[], activePatient, api, navigate }
 */
export async function runParsedCommand(parsed, ctx) {
  const { search, activePatient, api, navigate } = ctx;

  switch (parsed.action) {
    case "navigate_patient": {
      const q = parsed.target;
      if (!q) {
        return { type: "reply", text: "¿A qué paciente quieres abrir?" };
      }
      const matches = search(q, 5);
      if (matches.length === 0) {
        return {
          type: "reply",
          text: `No encontré ningún paciente con "${q}". Verifica el nombre.`,
        };
      }
      if (matches.length === 1) {
        // NO navegamos — mostramos widget inline con el paciente.
        return {
          type: "widget_patient",
          patient: matches[0],
        };
      }
      // Varios matches → chips
      return {
        type: "choose_patient",
        text: `Encontré ${matches.length} pacientes. ¿Cuál?`,
        options: matches.map((p) => ({
          id: p.id,
          label: p.full_name,
          sublabel: p.document_id || p.phone || "",
        })),
      };
    }
    case "navigate_new_patient": {
      navigate("/doctor/pacientes/nuevo");
      return { type: "reply", text: "Abriendo formulario de nuevo paciente." };
    }
    case "navigate_new_appointment": {
      const q = parsed.target;
      if (q) {
        const matches = search(q, 5);
        if (matches.length === 1) {
          navigate(`/doctor/agenda/nueva?patient_id=${matches[0].id}`);
          return {
            type: "reply_active",
            text: `Creando cita para ${matches[0].full_name}.`,
            activePatient: matches[0],
          };
        }
        if (matches.length > 1) {
          return {
            type: "choose_patient_for_appt",
            text: `¿Para cuál de estos pacientes?`,
            options: matches.map((p) => ({
              id: p.id,
              label: p.full_name,
              sublabel: p.document_id || p.phone || "",
            })),
          };
        }
      }
      navigate("/doctor/agenda/nueva");
      return { type: "reply", text: "Abriendo formulario de nueva cita." };
    }
    case "append_note": {
      const text = parsed.target;
      if (!text) {
        return { type: "reply", text: 'Dime qué nota agregar. Ej: "agrega esta nota paciente con fiebre alta".' };
      }
      if (!activePatient) {
        return {
          type: "reply",
          text: 'Primero abre un paciente para poder agregar la nota. Ej: "muéstrame a Juan".',
        };
      }
      // Si hay consulta en curso, la nota se acumula en la sesión.
      if (ctx.consultation && ctx.consultation.patient_id === activePatient.id) {
        ctx.consultationAddNote?.(text);
        return {
          type: "reply",
          text: `Nota añadida a la consulta en curso.`,
        };
      }
      try {
        await api.post(`/patients/${activePatient.id}/append-note`, { text });
        return {
          type: "reply",
          text: `Nota agregada al expediente de ${activePatient.full_name}.`,
        };
      } catch (e) {
        return {
          type: "reply",
          text: `No pude guardar la nota. ${e?.response?.data?.detail || "Intenta de nuevo."}`,
        };
      }
    }
    case "start_consultation": {
      const q = parsed.target;
      let patient = activePatient;
      if (q) {
        const matches = search(q, 5);
        if (matches.length === 1) patient = matches[0];
        else if (matches.length === 0) {
          return {
            type: "reply",
            text: `No encontré un paciente con "${q}".`,
          };
        } else {
          return {
            type: "reply",
            text: `Encontré varios pacientes con "${q}". Abre primero al paciente y luego di "inicia consulta".`,
          };
        }
      }
      if (!patient) {
        return {
          type: "reply",
          text: 'Primero abre un paciente. Ej: "muéstrame a Juan" y luego "inicia consulta".',
        };
      }
      if (ctx.consultation) {
        return {
          type: "reply",
          text: `Ya hay una consulta en curso con ${ctx.consultation.patient_name}. Finalízala o cancélala primero.`,
        };
      }
      ctx.consultationStart?.(patient);
      return {
        type: "reply",
        text: `Consulta iniciada con ${patient.full_name}. Usa "diagnóstico ...", "síntomas ...", "tratamiento ..." o "agrega esta nota ..." para registrar. Al terminar di "guarda la consulta".`,
      };
    }
    case "save_consultation": {
      if (!ctx.consultation) {
        return { type: "reply", text: "No hay ninguna consulta en curso." };
      }
      if (!ctx.consultation.diagnosis?.trim()) {
        return {
          type: "reply",
          text: 'Falta el diagnóstico. Di "diagnóstico ..." antes de guardar.',
        };
      }
      try {
        const payload = {
          patient_id: ctx.consultation.patient_id,
          diagnosis: ctx.consultation.diagnosis.trim(),
          symptoms: ctx.consultation.symptoms?.trim() || null,
          treatment: ctx.consultation.treatment?.trim() || null,
          notes: ctx.consultation.notes.length
            ? ctx.consultation.notes.join("\n")
            : null,
          cie10: ctx.consultation.cie10 || null,
          cie11: ctx.consultation.cie11 || null,
        };
        await api.post("/diagnoses", payload);
        // Si hay recetas acumuladas, crear una prescription vinculada.
        const rxItems = ctx.consultation.rxItems || [];
        let rxCreated = 0;
        if (rxItems.length > 0) {
          try {
            await api.post("/prescriptions", {
              patient_id: ctx.consultation.patient_id,
              diagnosis: ctx.consultation.diagnosis.trim(),
              notes: null,
              items: rxItems.map((it) => ({
                medication: it.medication,
                dosage: it.dosage || "",
                frequency: it.frequency || "",
                duration: it.duration || "",
              })),
              pharmacy_ids: [],
            });
            rxCreated = rxItems.length;
          } catch {
            /* si falla, el diagnóstico ya se guardó — informar */
          }
        }
        const name = ctx.consultation.patient_name;
        ctx.consultationEnd?.();
        const rxMsg = rxCreated
          ? ` Receta con ${rxCreated} medicamento${rxCreated === 1 ? "" : "s"} creada.`
          : "";
        return {
          type: "reply",
          text: `Consulta guardada en el expediente de ${name}.${rxMsg}`,
        };
      } catch (e) {
        return {
          type: "reply",
          text: `No pude guardar la consulta. ${e?.response?.data?.detail || "Intenta de nuevo."}`,
        };
      }
    }
    case "cancel_consultation": {
      if (!ctx.consultation) {
        return { type: "reply", text: "No hay ninguna consulta en curso." };
      }
      const name = ctx.consultation.patient_name;
      ctx.consultationEnd?.();
      return { type: "reply", text: `Consulta con ${name} descartada.` };
    }
    case "set_diagnosis":
    case "set_symptoms":
    case "set_treatment": {
      const text = parsed.target;
      if (!text) {
        return { type: "reply", text: "Falta el texto." };
      }
      if (!ctx.consultation) {
        return {
          type: "reply",
          text: 'Primero inicia una consulta. Di "inicia consulta".',
        };
      }
      const field =
        parsed.action === "set_diagnosis"
          ? "diagnosis"
          : parsed.action === "set_symptoms"
            ? "symptoms"
            : "treatment";
      const label =
        field === "diagnosis"
          ? "Diagnóstico"
          : field === "symptoms"
            ? "Síntomas"
            : "Tratamiento";
      ctx.consultationSetField?.(field, text);
      return { type: "reply", text: `${label} guardado en la consulta.` };
    }
    case "add_rx_item": {
      const spec = parsed.target;
      if (!spec) {
        return {
          type: "reply",
          text: 'Falta el medicamento. Ej: "agrega receta ibuprofeno 400mg cada 8h por 5 días".',
        };
      }
      if (!ctx.consultation) {
        return {
          type: "reply",
          text: 'Primero inicia una consulta. Di "inicia consulta" y luego agrega la receta.',
        };
      }
      const item = parseRxSpec(spec);
      if (!item?.medication) {
        return {
          type: "reply",
          text: `No entendí el medicamento en "${spec}".`,
        };
      }
      const missing = [];
      if (!item.dosage) missing.push("dosis");
      if (!item.frequency) missing.push("frecuencia");
      if (!item.duration) missing.push("duración");
      ctx.consultationAddRxItem?.(item);
      const label = `${item.medication}${item.dosage ? ` ${item.dosage}` : ""}${item.frequency ? ` · ${item.frequency}` : ""}${item.duration ? ` · ${item.duration}` : ""}`;
      const warn = missing.length
        ? ` Falta ${missing.join(", ")} — completa antes de guardar.`
        : "";
      return {
        type: "reply",
        text: `Receta añadida a la consulta: ${label}.${warn}`,
      };
    }
    case "navigate_agenda": {
      navigate("/doctor/agenda");
      return { type: "reply", text: "Abriendo la agenda." };
    }
    case "navigate_patients": {
      navigate("/doctor/pacientes");
      return { type: "reply", text: "Abriendo la lista de pacientes." };
    }
    case "navigate_prescriptions": {
      navigate("/doctor/recetas");
      return { type: "reply", text: "Abriendo las recetas." };
    }
    case "show_help": {
      return { type: "help" };
    }
    default:
      return { type: "reply", text: "No reconocí el comando." };
  }
}

export const HELP_COMMANDS = [
  {
    label: "Abrir expediente",
    examples: ['"muéstrame el paciente Juan"', '"ficha de Ana Pérez"'],
  },
  {
    label: "Consulta en curso",
    examples: [
      '"inicia consulta"',
      '"diagnóstico bronquitis aguda"',
      '"síntomas tos y fiebre"',
      '"tratamiento amoxicilina 500mg cada 8h"',
      '"agrega receta ibuprofeno 400mg cada 8h por 5 días"',
      '"guarda la consulta"',
      '"cancela la consulta"',
    ],
  },
  {
    label: "Crear paciente",
    examples: ['"crear paciente"', '"nuevo paciente"'],
  },
  {
    label: "Crear cita",
    examples: ['"nueva cita"', '"crear cita para Juan"'],
  },
  {
    label: "Agregar nota",
    examples: ['"agrega esta nota paciente con fiebre alta"'],
  },
  {
    label: "Navegar",
    examples: ['"abrir agenda"', '"abrir recetas"', '"ver pacientes"'],
  },
];
